/* Core & libraries */
import React, { useEffect, useState } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import { Link, useNavigate } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';

/* Helpers, constants, custom methods, internal libraries */
import { useGlobalContext, useGlobalUpdateContext } from "../../../../contexts/globalContext";
import { usePeopleService } from "../../../../services/peopleService";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import { colors } from "../../../../constants/colors";
import { TEAM_DETAILS_ROUTE, TEAM_DETAILS_SETTINGS_ROUTE } from "../../../../constants/routes";

/* Components */
import UCGrid from "../../../ucgrid";
import TeamDefaultTooltip from "../../../../components/teamDefaultTooltip"
import UCButton from "../../../buttons";
import { ReactComponent as MatercardIcon } from "../../../../resources/images/aa-mastercard.svg"
import { ReactComponent as VisaIcon } from '../../../../resources/images/aa-visa.svg';
import { ReactComponent as AmexIcon } from '../../../../resources/images/aa-amex.svg';
import { ReactComponent as DiscoverIcon } from '../../../../resources/images/aa-discover.svg';
import { ReactComponent as DinnersClubIcon } from '../../../../resources/images/aa-dinnersclub.svg';
import { ReactComponent as UATPIcon } from '../../../../resources/images/aa-uatp.svg';
import { ReactComponent as JcbIcon } from '../../../../resources/images/aa-jcb.svg';
import { ReactComponent as UnionPayIcon } from '../../../../resources/images/aa-unionpay.svg';
import { ReactComponent as GenericPayIcon } from '../../../../resources/images/aa-genericpay.svg';
import { ReactComponent as ViasIcon } from '../../../../resources/images/cc-alaska.svg';
import { ReactComponent as PlusSign } from '../../../../resources/images/plus.svg';

/* Styles */
import styles from "./styles.js"

const TeamsGrid = (props) => {
  const {gridOptions, setGridOptions, data, deleteTeam} = props;
  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();

  const globalProperties  = useGlobalContext();
  const setGlobalProperties  = useGlobalUpdateContext();
  const { checkPermission } = useHasPermission();

  const { getSharedLink } = usePeopleService();

  const [selectedRow, setSelectedRow] = useState(null);

  const actionCallback = (actionType) => {
  //   switch (actionType) {
  //
  //   }
  }


  const handleInvitePeople = async (row) =>{
    const response = await getSharedLink(row.entityID, `invitePeopleButton${row.entityID}`);
    if (response) {
      setGlobalProperties({
        ...globalProperties,
        invitePeopleModal: {
          showInvitePeopleModal: true,
          teamName: response.teamName,
          sharedLink: response.sharedLink,
          active: response.active,
          requiresDomain: response.requiresDomain,
          teamId: row.entityID,
          isNotDefault: !row.default
        }
      })
    }
  }

  let customProps = {};
  if (checkPermission("F_INVITE_USER")) {
    customProps.actionsComponent = (row) => (
      <UCButton
        type="default"
        className={classes.invBtn}
        loadingStateStyling={classes.invBtnLoading}
        text={<FormattedMessage id="people.inviteButton"/>}
        onClick={() => handleInvitePeople(row)}
        spinner={{
          name: `invitePeopleButton${row.entityID}`,
          size: 20,
        }}
      />
    )
  }

  const getCardIcon = cardType => {
    switch(cardType) {
      case "CA":
        return <MatercardIcon/>
      case "VI":
        return <VisaIcon/>
      case "AX":
        return <AmexIcon/>
      case "DS":
        return <DiscoverIcon/>
      case "DC":
        return <DinnersClubIcon/>
      case "TP":
        return <UATPIcon/>
      case "JC":
        return <JcbIcon/>
      case "CU":
        return <UnionPayIcon/>
      case "VIAS":
      case "AS":
        return <ViasIcon/>
      default:
        return <GenericPayIcon/>
    }
  }

  const getLinkRoute = row => {
    if(checkPermission("F_TEAMDASHBOARD_VIEWSETTINGTAB")) return TEAM_DETAILS_SETTINGS_ROUTE.replace(":teamId", row.entityID)
    else return TEAM_DETAILS_ROUTE.replace(":teamId", row.entityID)
  }

  return (
    <div className={classes.teamsGrid}>
      <UCGrid
        gridName="teamsGrid"
        data={ data?.results }
        columns={ data?.columns }
        actions={ data?.actions }
        actionsWidth={"auto"}
        handlePageChange={(page) => {setGridOptions({
          ...gridOptions,
          page
        })
        page > 1 ? navigate(`/app/teams?page=${page}`) : navigate(`/app/teams`)
        }}
        setSelectedRow={setSelectedRow}
        actionCallback={actionCallback}
        spinner="teamsGridSpinner"
        customColumns={{
          groupName: (row) => (
            <div className={classes.tooltipWrapper}>
              <Link to={getLinkRoute(row)} className={classes.link}>
                {row.groupName}
              </Link>
              {row.default ? <TeamDefaultTooltip /> : ""}
            </div>
          ),
          paymentCards: (row) => (
            row?.paymentCards?.length ?
              <Link to={getLinkRoute(row)} className={`${classes.cardContainer} ${classes.link}`}>
                <div className={classes.assignedName}>
                  <>
                    <div className={classes.cardIcon}>{getCardIcon(row.paymentCards[0]?.fopCardType)}</div>
                    <span>{`**** ${row.paymentCards[0]?.fopLastFour}`}</span>
                    {row?.paymentCards.length > 1 &&
                      <div className={classes.extraCards}>
                        <PlusSign/>
                        <p>{row?.paymentCards.length - 1}</p>
                      </div>
                    }
                  </>
                </div>
              </Link>
              : <FormattedMessage id="teamsGrid.personalCard"/>
          ),
          numberOfUsers: (row) => (
            <NumericFormat
              value={row.numberOfUsers}
              thousandSeparator=","
              decimalScale={0}
              fixedDecimalScale
              displayType="text"
              renderText={(value) => <div className={classes.numberOfUsers}>{value}</div>}
            />
          ),
          policyName: (row) => (
            row?.policyName ?
              <div>{row?.policyName}</div>
            : <div className={classes.noPolicy}><FormattedMessage id="teamsGrid.noPolicy"/></div>
          ),
        }}
        columnCustomWidth={{
          paymentCards: "271px !important"
        }}
        columnCustomProps={[
          {
            columnName: "actions",
            allowOverflow: true,
            style: {
              display: 'flex',
              justifyContent: 'end'
            }
          }
        ]}
        {...customProps}
        defaultSort={data?.defaultSort}
        handleSort={(column, sortDirection) =>
          setGridOptions({
            ...gridOptions,
            sortby: column.id,
            sortdirection: sortDirection === "desc" ? "dsc" : sortDirection,
          })
        }
        onRowClickRoute={row => getLinkRoute(row)}
      />
    </div>
  )
}

export default TeamsGrid;
